import { getNewDealCounts } from '@/lib/api/sales/deals-api'
import { getPipelines, PIPELINE_TYPE_LABELS, SalesPipeline } from '@/lib/api/sales/pipelines-api'
import { SocketContext } from '@/lib/providers/socket-provider'
import { ensurePipelineLastSeen, PIPELINE_SEEN_EVENT } from '@/lib/sales/pipeline-last-seen'
import { useAuth } from '@/store/hooks'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { MdFilterList, MdRefresh, MdTrendingUp } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'

const PIPELINE_REFRESH_EVENT = 'sales:pipelines:refresh'

export function dispatchPipelineRefresh() {
    window.dispatchEvent(new CustomEvent(PIPELINE_REFRESH_EVENT))
}

export default function SalesPipelineDropdown() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const [pipelines, setPipelines] = useState<SalesPipeline[]>([])
    const [loading, setLoading] = useState(false)
    const { socket } = useContext(SocketContext)
    // Oportunidades que entraram em cada funil desde a última visita do usuário
    const [newCounts, setNewCounts] = useState<Record<number, number>>({})
    const countsTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

    const loadPipelines = useCallback(async () => {
        if (!user?.companyId) return
        setLoading(true)
        try {
            const response = await getPipelines()
            if (response.success) {
                setPipelines(response.result)
            }
        } finally {
            setLoading(false)
        }
    }, [user?.companyId])

    useEffect(() => {
        loadPipelines()
    }, [loadPipelines])

    useEffect(() => {
        const handler = () => loadPipelines()
        window.addEventListener(PIPELINE_REFRESH_EVENT, handler)
        return () => window.removeEventListener(PIPELINE_REFRESH_EVENT, handler)
    }, [loadPipelines])

    const activeIds = pipelines.filter(p => p.active).map(p => p.id).join(',')

    const loadNewCounts = useCallback(async () => {
        if (!user?.id || !activeIds) return
        const ids = activeIds.split(',').map(Number)
        const since = ensurePipelineLastSeen(user.id, ids)
        const res = await getNewDealCounts(Object.fromEntries(ids.map(id => [id, since[id]])))
        // Sem permissão de ler oportunidades, a resposta é 403: fica sem badge
        if (res.success && res.result) setNewCounts(res.result)
    }, [user?.id, activeIds])

    useEffect(() => {
        loadNewCounts()
    }, [loadNewCounts])

    // Entrar num funil zera a contagem dele
    useEffect(() => {
        const handler = () => loadNewCounts()
        window.addEventListener(PIPELINE_SEEN_EVENT, handler)
        return () => window.removeEventListener(PIPELINE_SEEN_EVENT, handler)
    }, [loadNewCounts])

    // Tempo real: o canal é da organização inteira; junta a rajada numa consulta só
    useEffect(() => {
        if (!socket || !user?.organizationId) return
        const event = `deal-updated-org-${user.organizationId}`
        const handler = () => {
            if (countsTimer.current) clearTimeout(countsTimer.current)
            countsTimer.current = setTimeout(loadNewCounts, 1500)
        }
        socket.on(event, handler)
        return () => {
            if (countsTimer.current) clearTimeout(countsTimer.current)
            socket.off(event, handler)
        }
    }, [socket, user?.organizationId, loadNewCounts])

    const handleClick = (pipelineId: number) => {
        navigate(`/sales/pipelines/${pipelineId}`)
        const el = document.activeElement as HTMLElement
        if (el) el.blur()
    }

    const activePipelines = pipelines.filter((p) => p.active)
    const totalNew = activePipelines.reduce((sum, p) => sum + (newCounts[p.id] ?? 0), 0)

    if (activePipelines.length === 0 && !loading) return null

    return (
        <div className="dropdown dropdown-bottom dropdown-end max-sm:!static">
            <div
                tabIndex={0}
                role="button"
                className="btn btn-ghost gap-1.5"
                title={
                    totalNew > 0
                        ? `Funis de vendas: ${totalNew} ${totalNew === 1 ? 'oportunidade nova' : 'oportunidades novas'}`
                        : 'Funis de vendas'
                }
            >
                <MdFilterList className="text-xl text-secondary" />
                <span className="hidden lg:inline text-sm font-medium"></span>
                {/* Oportunidades que entraram nos funis desde a última visita a cada um */}
                {totalNew > 0 && (
                    <span className="badge badge-error badge-sm text-white font-bold min-w-[20px]">
                        {totalNew > 99 ? '99+' : totalNew}
                    </span>
                )}
            </div>

            <ul
                tabIndex={0}
                className="dropdown-content menu flex-nowrap overflow-hidden bg-base-100 rounded-xl shadow-xl border border-base-300 z-[100] p-0 max-sm:!inset-x-2 max-sm:!w-auto"
                style={{ width: '320px' }}
            >
                <div className="flex items-center justify-between px-4 py-3 border-b border-base-300">
                    <h3 className="font-semibold text-sm text-base-content">Funis de Vendas</h3>
                    <button
                        className="btn btn-ghost btn-xs"
                        onClick={(e) => {
                            e.stopPropagation()
                            loadPipelines()
                        }}
                        title="Atualizar"
                    >
                        <MdRefresh className={`text-base ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>

                <div className="p-2 max-h-[60vh] overflow-y-auto">
                    {loading && activePipelines.length === 0 ? (
                        <div className="px-4 py-6 text-center">
                            <span className="loading loading-spinner loading-sm" />
                        </div>
                    ) : activePipelines.length === 0 ? (
                        <div className="px-4 py-4 text-center text-base-content/50 text-sm">
                            Nenhum funil encontrado
                        </div>
                    ) : (
                        activePipelines.map((pipeline) => {
                            const dealCount = (pipeline as any)._count?.deals ?? 0
                            const stageCount = pipeline.stages?.length ?? (pipeline as any)._count?.stages ?? 0
                            const typeLabel = PIPELINE_TYPE_LABELS[pipeline.type] || pipeline.type

                            return (
                                <button
                                    key={pipeline.id}
                                    className="flex items-start gap-3 px-3 py-2.5 rounded-lg hover:bg-base-200 w-full text-left"
                                    onClick={() => handleClick(pipeline.id)}
                                >
                                    <div className="mt-0.5">
                                        <MdTrendingUp className="text-lg text-secondary" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <span className="font-medium text-sm text-base-content truncate min-w-0">
                                                {pipeline.name}
                                            </span>
                                            {pipeline.isDefault && (
                                                <span className="badge badge-xs badge-secondary shrink-0">Padrão</span>
                                            )}
                                        </div>
                                        {/* Filial em linha própria, cortada: o nome da empresa é longo e,
                                            como selo (que não quebra), vazava para fora do dropdown. */}
                                        <div
                                            className="text-xs text-base-content/60 truncate mt-0.5"
                                            title={pipeline.company?.name ?? 'Organização inteira'}
                                        >
                                            {pipeline.company?.name ?? 'Organização inteira'}
                                        </div>
                                        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                                            <span className="text-xs text-base-content/50">{typeLabel}</span>
                                            <span className="text-xs text-base-content/50">
                                                {stageCount} {stageCount === 1 ? 'etapa' : 'etapas'}
                                            </span>
                                            <span className="text-xs font-medium text-secondary">
                                                {dealCount} {dealCount === 1 ? 'oportunidade' : 'oportunidades'}
                                            </span>
                                        </div>
                                    </div>
                                </button>
                            )
                        })
                    )}
                </div>

                <div className="border-t border-base-300 p-2">
                    <button
                        className="btn btn-ghost btn-sm btn-block text-secondary"
                        onClick={() => {
                            navigate('/sales/pipelines')
                            const el = document.activeElement as HTMLElement
                            if (el) el.blur()
                        }}
                    >
                        Ver todos os funis
                    </button>
                </div>
            </ul>
        </div>
    )
}
